import React, { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { Card, ErrorState, IconButton, SegmentedControl, Skeleton } from '@liftango/ui-elements';
import { Icon } from '@liftango/ui-icons';
import { opsColors, opsFontFamily, opsFontSize, opsFontWeight, opsRadius, opsSpace } from '../../../../../theme/opsTokens';
import { parseResultTime, type ShiftSummary } from '../../../../../services/simulation/runReport';
import type { UnmatchedRide } from '../../../../../types/simulation';
import type { RunReport } from '../useRunReport';
import { formatClock, formatNumber } from './reportFormat';
import { kmLabel, percentLabel, rowActivation, seatLabel, vehicleName } from './railLabels';
import { SourceProblem } from './SourceProblem';
import { VehicleDetail } from './VehicleDetail';

type RailTab = 'vehicles' | 'unmatched';

export interface ResultsRailProps {
  readonly report: RunReport;
  readonly selectedShiftId: string | null;
  readonly onSelectShift: (shiftId: string | null) => void;
  /** Moves the report's clock to an event. */
  readonly onJumpTo: (timeMs: number) => void;
  /** Vehicles left off the map. */
  readonly hiddenShiftIds: ReadonlySet<string>;
  readonly onToggleShown: (shiftId: string) => void;
}

const LOAD_BAR = { width: '32px', height: '4px' } as const;
const KM_SKELETON = { width: '32px', height: '12px' } as const;
const EYE_CELL = { width: '1%' } as const;
const EYE_ICON_SIZE = 14;
const MONO = { fontFamily: opsFontFamily.mono, fontSize: opsFontSize.sm } as const;
const HEAD_CELL = {
  backgroundColor: opsColors.neutral.panelAlt,
  color: opsColors.neutral.inkMute,
  fontSize: opsFontSize.meta,
  fontWeight: opsFontWeight.bold,
  textTransform: 'uppercase',
} as const;
const HINT = { px: opsSpace[3], py: opsSpace[2], color: opsColors.neutral.inkMute, fontSize: opsFontSize.meta } as const;

const isRailTab = (value: string): value is RailTab => value === 'vehicles' || value === 'unmatched';

function paxCount(passengers: UnmatchedRide['passengers']): number {
  return (passengers ?? []).reduce((sum, passenger) => sum + (passenger.number_of_passengers ?? 0), 0);
}

export const ResultsRail = memo(function ResultsRail({ report, selectedShiftId, onSelectShift, onJumpTo, hiddenShiftIds, onToggleShown }: ResultsRailProps) {
  const { t } = useTranslation('simulation');
  const [tab, setTab] = useState<RailTab>('vehicles');
  const usedShifts = useMemo(() => report.derived?.shifts.filter((shift) => shift.trips > 0) ?? [], [report.derived]);
  const unmatched = report.unmatched.status === 'ready' ? report.unmatched.data : null;
  const selected = usedShifts.find((shift) => shift.shiftId === selectedShiftId) ?? null;

  return (
    <Card padding="none" data-testid="sim-results.report.rail" style={{ display: 'flex', flexDirection: 'column', minHeight: 0, width: '100%' }}>
      <Box sx={{ p: opsSpace[3], borderBottom: `1px solid ${opsColors.neutral.line}` }}>
        <SegmentedControl
          aria-label={t('results.report.rail.tabsLabel')}
          value={tab}
          onChange={(value) => isRailTab(value) && setTab(value)}
          options={[
            { value: 'vehicles', label: t('results.report.rail.vehiclesTab', { count: usedShifts.length }) },
            { value: 'unmatched', label: t('results.report.rail.unmatchedTab', { count: unmatched?.length ?? 0 }) },
          ]}
        />
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {tab === 'vehicles' ? (
          <VehiclesPane
            report={report}
            shifts={usedShifts}
            selected={selected}
            onSelectShift={onSelectShift}
            onJumpTo={onJumpTo}
            hiddenShiftIds={hiddenShiftIds}
            onToggleShown={onToggleShown}
          />
        ) : (
          <UnmatchedPane report={report} rides={unmatched} onJumpTo={onJumpTo} />
        )}
      </Box>
    </Card>
  );
});

interface VehiclesPaneProps {
  readonly report: RunReport;
  readonly shifts: readonly ShiftSummary[];
  readonly selected: ShiftSummary | null;
  readonly onSelectShift: (shiftId: string | null) => void;
  readonly onJumpTo: (timeMs: number) => void;
  /** Vehicles left off the map. */
  readonly hiddenShiftIds: ReadonlySet<string>;
  readonly onToggleShown: (shiftId: string) => void;
}

function VehiclesPane({ report, shifts, selected, onSelectShift, onJumpTo, hiddenShiftIds, onToggleShown }: VehiclesPaneProps) {
  const { t, i18n } = useTranslation('simulation');
  const locale = i18n.language;
  if (!report.derived) return <SourceProblem report={report} sources={['activities', 'vehicles']} />;
  if (selected) return <VehicleDetail report={report} shift={selected} onBack={() => onSelectShift(null)} onJumpTo={onJumpTo} />;

  return (
    <>
      <Box sx={HINT}>{t('results.report.rail.vehiclesHint')}</Box>
      {report.paths.status === 'error' ? (
        <Box data-testid="sim-results.report.rail.paths-error">
          <ErrorState title={t('results.report.errors.paths')} onRetry={() => report.retry('paths')} retryLabel={t('results.report.retry')} />
        </Box>
      ) : null}
      <TableContainer>
        <Table stickyHeader size="small" aria-label={t('results.report.rail.vehiclesTable')} sx={{ '& .MuiTableCell-root': { px: opsSpace[2] } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...HEAD_CELL, ...EYE_CELL }} aria-label={t('results.report.rail.columns.onMap')} />
              <TableCell sx={HEAD_CELL}>{t('results.report.rail.columns.vehicle')}</TableCell>
              <TableCell sx={HEAD_CELL} align="right">{t('results.report.rail.columns.trips')}</TableCell>
              <TableCell sx={HEAD_CELL} align="right">{t('results.report.rail.columns.pax')}</TableCell>
              <TableCell sx={HEAD_CELL} align="right">{t('results.report.rail.columns.km')}</TableCell>
              <TableCell sx={HEAD_CELL}>{t('results.report.rail.columns.peakLoad')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shifts.map((shift) => (
              <VehicleRow
                key={shift.shiftId}
                report={report}
                shift={shift}
                t={t}
                locale={locale}
                onSelect={() => onSelectShift(shift.shiftId)}
                shown={!hiddenShiftIds.has(shift.shiftId)}
                onToggleShown={() => onToggleShown(shift.shiftId)}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

interface VehicleRowProps {
  readonly report: RunReport;
  readonly shift: ShiftSummary;
  readonly t: TFunction;
  readonly locale: string;
  readonly onSelect: () => void;
  readonly shown: boolean;
  readonly onToggleShown: () => void;
}

// The eye sits inside a clickable row; stop its click and keys from also opening the vehicle.
const keepInCell = (event: React.SyntheticEvent) => event.stopPropagation();

function VehicleRow({ report, shift, t, locale, onSelect, shown, onToggleShown }: VehicleRowProps) {
  const name = vehicleName(shift, t);
  const km = kmLabel(report, shift.shiftId, t, locale);
  const standard = shift.peakLoadFactor.standard;
  return (
    <TableRow hover aria-label={t('results.report.rail.openVehicle', { name })} data-testid={`sim-results.report.rail.vehicle.${shift.ordinal}`} {...rowActivation(onSelect)}>
      <TableCell sx={EYE_CELL} onClick={keepInCell} onKeyDown={keepInCell}>
        <IconButton
          data-testid="sim-results.report.rail.toggle-shown"
          label={t(shown ? 'results.report.rail.hideOnMap' : 'results.report.rail.showOnMap', { name })}
          aria-pressed={shown}
          icon={<Icon name={shown ? 'eye' : 'eye-slash'} size={EYE_ICON_SIZE} />}
          onClick={onToggleShown}
        />
      </TableCell>
      <TableCell sx={shown ? undefined : { color: opsColors.neutral.inkMute }}>
        <Box sx={{ fontWeight: opsFontWeight.semibold, whiteSpace: 'nowrap' }}>{name}</Box>
        <Box sx={{ color: opsColors.neutral.inkMute, fontSize: opsFontSize.meta }}>{seatLabel(shift, t)}</Box>
      </TableCell>
      <TableCell align="right" sx={MONO}>{formatNumber(shift.trips, locale)}</TableCell>
      <TableCell align="right" sx={MONO}>{formatNumber(shift.pax, locale)}</TableCell>
      <TableCell align="right" sx={MONO} data-testid="sim-results.report.rail.km">
        {km.kind === 'loading' ? <Skeleton {...KM_SKELETON} /> : km.text}
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: opsSpace[2], ...MONO }}>
          <Box role="presentation" sx={{ ...LOAD_BAR, borderRadius: opsRadius.pill, backgroundColor: opsColors.neutral.line, overflow: 'hidden' }}>
            <Box sx={{ width: `${Math.min(1, standard ?? 0) * 100}%`, height: '100%', backgroundColor: opsColors.neutral.inkSoft }} />
          </Box>
          <span>{percentLabel(standard, t, locale)}</span>
        </Box>
      </TableCell>
    </TableRow>
  );
}

function UnmatchedPane({ report, rides, onJumpTo }: { report: RunReport; rides: readonly UnmatchedRide[] | null; onJumpTo: (timeMs: number) => void }) {
  const { t, i18n } = useTranslation('simulation');
  const locale = i18n.language;
  const sorted = useMemo(
    () =>
      (rides ?? [])
        .map((ride) => ({ ride, requestMs: parseResultTime(ride.request_time) }))
        .sort((a, b) => (a.requestMs ?? Infinity) - (b.requestMs ?? Infinity)),
    [rides]
  );
  if (!rides) return <SourceProblem report={report} sources={['unmatched']} />;

  return (
    <>
      <Box sx={HINT}>{t('results.report.rail.unmatchedHint')}</Box>
      <TableContainer>
        <Table stickyHeader size="small" aria-label={t('results.report.rail.unmatchedTable')}>
          <TableHead>
            <TableRow>
              <TableCell sx={HEAD_CELL}>{t('results.report.rail.columns.time')}</TableCell>
              <TableCell sx={HEAD_CELL}>{t('results.report.rail.columns.request')}</TableCell>
              <TableCell sx={HEAD_CELL} align="right">{t('results.report.rail.columns.pax')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map(({ ride, requestMs }, index) => (
              <TableRow
                key={ride.ride_id ?? `unmatched-${index}`}
                hover
                data-testid="sim-results.report.rail.unmatched-row"
                aria-label={requestMs == null ? undefined : t('results.report.rail.jumpToRequest', { time: formatClock(requestMs, locale) })}
                {...(requestMs == null ? {} : rowActivation(() => onJumpTo(requestMs)))}
              >
                <TableCell sx={MONO} data-testid="sim-results.report.rail.unmatched-time">
                  {requestMs == null ? t('results.report.noValue') : formatClock(requestMs, locale)}
                </TableCell>
                <TableCell sx={MONO} data-testid="sim-results.report.rail.unmatched-request">
                  {ride.external_reference_id ?? t('results.report.noValue')}
                </TableCell>
                <TableCell align="right" sx={MONO} data-testid="sim-results.report.rail.unmatched-pax">
                  {formatNumber(paxCount(ride.passengers), locale)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
